import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {boolean} */
  #isHandlingClick = false;
  /** @type {Set<string>} */
  #preloadedSwatchUrls = new Set();
  /** @type {Map<string, Promise<Element | null>>} */
  #ongoingLoads = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.addEventListener('pointerdown', this.#prefetchOnPointerDown, { passive: true });
    this.addEventListener('touchstart', this.#prefetchOnPointerDown, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.removeEventListener('pointerdown', this.#prefetchOnPointerDown, { passive: true });
    this.removeEventListener('touchstart', this.#prefetchOnPointerDown, { passive: true });
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = modalContent.querySelector('variant-picker');
    if (variantPicker instanceof VariantPicker) {
      variantPicker.updateVariantPicker(newHtml);
    }
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isHandlingClick) return;
    this.#isHandlingClick = true;
    this.#setButtonLoading(true);

    const currentUrl = this.productPageUrl;

    let dialogComponent = null;
    try {
      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        productGrid = await this.#getOrLoadProductGrid(currentUrl);
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }

      dialogComponent = this.#openQuickAddModal();
    } finally {
      if (dialogComponent) {
        this.#waitForDialogToBegin(dialogComponent);
      } else {
        this.#setButtonLoading(false);
        this.#isHandlingClick = false;
      }
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
        this.#setButtonLoading(false);
        this.#isHandlingClick = false;
      },
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return null;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
    return dialogComponent;
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, { allowConcurrent = false } = {}) {
    if (!productPageUrl) return null;

    if (!allowConcurrent) {
      // Abort any existing fetch if we are initiating a primary load.
      this.#abortController?.abort();
      this.#abortController = new AbortController();
    }

    try {
      const controller = /** @type {AbortController} */ (
        allowConcurrent ? new AbortController() : this.#abortController
      );
      const response = await fetch(productPageUrl, {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (!allowConcurrent) {
        this.#abortController = null;
      }
    }
  }

  async #getOrLoadProductGrid(url, allowConcurrent = false) {
    if (!url) return null;
    const cached = this.#cachedContent.get(url);
    if (cached) return cached;

    const ongoing = this.#ongoingLoads.get(url);
    if (ongoing) return ongoing;

    const loadPromise = this.#fetchAndCacheProductGrid(url, allowConcurrent);
    this.#ongoingLoads.set(url, loadPromise);
    loadPromise.finally(() => {
      this.#ongoingLoads.delete(url);
    });
    return loadPromise;
  }

  async #fetchAndCacheProductGrid(url, allowConcurrent) {
    const html = await this.fetchProductPage(url, { allowConcurrent });
    if (!html) return null;

    const gridElement = html.querySelector('[data-product-grid-content]');
    if (!gridElement) return null;

    const cachedGrid = /** @type {Element} */ (gridElement.cloneNode(true));
    this.#cachedContent.set(url, cachedGrid);
    return cachedGrid;
  }

  #prefetchOnPointerDown = (event) => {
    if (this.#isHandlingClick) return;
    if (event.type === 'pointerdown' && event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    const quickAddButton = event.target.closest('.quick-add__button--choose');
    if (!quickAddButton) return;

    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl)) return;

    this.#getOrLoadProductGrid(currentUrl, true).catch(() => {});
  };

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    this.#preloadVariantOptionImages(productGrid);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  #preloadVariantOptionImages(productGrid) {
    if (!productGrid) return;

    const swatches = productGrid.querySelectorAll('.swatch');

    for (const swatch of swatches) {
      const styleAttribute = swatch.getAttribute('style');
      if (!styleAttribute || !styleAttribute.includes('url(')) continue;

      const urlRegex = /url\((['"]?)(.*?)\1\)/g;
      let match;

      while ((match = urlRegex.exec(styleAttribute))) {
        const url = match[2];
        if (!url || this.#preloadedSwatchUrls.has(url)) continue;

        const image = new Image();
        image.src = url;
        this.#preloadedSwatchUrls.add(url);
      }
    }
  }

  #setButtonLoading(isLoading) {
    if (isLoading) {
      this.dataset.loading = 'true';
    } else {
      delete this.dataset.loading;
    }

    const buttons = this.querySelectorAll('.quick-add__button--choose');
    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.classList.toggle('is-loading', isLoading);
      button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      button.disabled = isLoading;

      if (!isLoading) {
        button.getBoundingClientRect();
      }
    }
  }

  /**
   * Keeps the loading state until the dialog actually begins animating open.
   * @param {QuickAddDialog} dialogComponent
   */
  #waitForDialogToBegin(dialogComponent) {
    const dialogElement = dialogComponent.refs?.dialog;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      this.#setButtonLoading(false);
      this.#isHandlingClick = false;
      if (dialogElement instanceof HTMLDialogElement) {
        dialogElement.removeEventListener('animationstart', onStart);
        dialogElement.removeEventListener('transitionstart', onStart);
      }
    };

    const onStart = () => {
      finish();
    };

    if (dialogElement instanceof HTMLDialogElement) {
      dialogElement.addEventListener('animationstart', onStart, { once: true });
      dialogElement.addEventListener('transitionstart', onStart, { once: true });
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(finish);
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
